import {
  ArchiveBoxIcon,
  ArrowCounterClockwiseIcon,
  ArrowUpRightIcon,
  FilePlusIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { type FormEvent, useCallback, useEffect, useState } from "react";

import { ApiError, apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { SelectField, TextField } from "../components/Field";
import { LoadingState } from "../components/LoadingState";
import { PageHeader } from "../components/PageHeader";
import { Link, useNavigate } from "../router";
import type { Page } from "../types";

export function PagesPage() {
  const [activePages, setActivePages] = useState<Page[] | null>(null);
  const [archivedPages, setArchivedPages] = useState<Page[] | null>(null);
  const [view, setView] = useState<"active" | "archived">("active");
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const load = useCallback(() => {
    setError("");
    return Promise.all([
      apiRequest<Page[]>("/api/v1/admin/pages"),
      apiRequest<Page[]>("/api/v1/admin/pages?archived=true"),
    ])
      .then(([active, archived]) => {
        setActivePages(active);
        setArchivedPages(archived);
      })
      .catch(() => setError("Не удалось загрузить страницы"));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pages = view === "active" ? activePages : archivedPages;
  const archivedProjects =
    archivedPages?.filter((page) => page.template_key === "project") ?? [];

  async function restorePage(page: Page) {
    setBusyId(page.id);
    setError("");
    setNotice("");
    try {
      await apiRequest(`/api/v1/admin/pages/${page.id}/restore`, {
        method: "POST",
      });
      await load();
      setNotice(`Страница «${page.internal_name}» восстановлена`);
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось восстановить страницу",
      );
    } finally {
      setBusyId("");
    }
  }

  async function deletePermanently(page: Page) {
    if (
      !window.confirm(
        `Удалить «${page.internal_name}» навсегда? Версии и локализации восстановить будет невозможно.`,
      )
    ) {
      return;
    }
    setBusyId(page.id);
    setError("");
    setNotice("");
    try {
      await apiRequest(`/api/v1/admin/pages/${page.id}/permanent`, {
        method: "DELETE",
      });
      await load();
      setNotice(`Страница «${page.internal_name}» удалена навсегда`);
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось окончательно удалить страницу",
      );
    } finally {
      setBusyId("");
    }
  }

  async function purgeArchivedProjects() {
    if (
      !window.confirm(
        `Удалить навсегда все архивные кейсы (${archivedProjects.length})? Это действие нельзя отменить.`,
      )
    ) {
      return;
    }
    setBusyId("projects");
    setError("");
    setNotice("");
    try {
      const result = await apiRequest<{ deleted_count: number }>(
        "/api/v1/admin/pages/archived/projects",
        { method: "DELETE" },
      );
      await load();
      setNotice(`Архивные кейсы удалены: ${result.deleted_count}`);
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось удалить архивные кейсы",
      );
    } finally {
      setBusyId("");
    }
  }

  return (
    <div className="page">
      <PageHeader
        actions={
          <>
            <Button
              onClick={() => {
                setView((current) =>
                  current === "active" ? "archived" : "active",
                );
                setShowCreate(false);
                setError("");
                setNotice("");
              }}
              tone={view === "archived" ? "secondary" : "quiet"}
            >
              <ArchiveBoxIcon size={17} />
              {view === "archived"
                ? "К активным"
                : `Архив${archivedPages?.length ? ` · ${archivedPages.length}` : ""}`}
            </Button>
            {view === "active" ? (
              <Button onClick={() => setShowCreate((value) => !value)}>
                <PlusIcon size={17} /> Новая страница
              </Button>
            ) : archivedProjects.length > 0 ? (
              <Button
                disabled={Boolean(busyId)}
                onClick={() => void purgeArchivedProjects()}
                tone="danger"
              >
                <TrashIcon size={17} /> Удалить все кейсы
              </Button>
            ) : null}
          </>
        }
        description={
          view === "active"
            ? "Структура, языковые версии, черновики и опубликованные снимки сайта."
            : "Удалённые из рабочего списка страницы. Их можно восстановить или стереть окончательно."
        }
        eyebrow="CMS"
        title={view === "active" ? "Страницы" : "Архив страниц"}
      />
      {showCreate && view === "active" && (
        <CreatePage onCancel={() => setShowCreate(false)} />
      )}
      {error && <div className="notice notice--error">{error}</div>}
      {notice && <div className="notice notice--success">{notice}</div>}
      {!pages ? (
        <LoadingState />
      ) : pages.length === 0 ? (
        <EmptyState
          description={
            view === "active"
              ? "Создайте страницу и добавьте её первую языковую версию."
              : "Архивированные страницы появятся здесь."
          }
          title={view === "active" ? "Страниц пока нет" : "Архив пуст"}
        />
      ) : view === "active" ? (
        <section className="page-list">
          <div className="page-list__head">
            <span>Название</span>
            <span>Ключ</span>
            <span>Шаблон</span>
            <span>Обновлено</span>
            <span />
          </div>
          {pages.map((page) => (
            <Link
              className="page-list__row"
              key={page.id}
              to={`/pages/${page.id}`}
            >
              <span className="page-name">
                <i>
                  <FilePlusIcon size={19} />
                </i>
                <strong>{page.internal_name}</strong>
              </span>
              <span className="mono">{page.page_key}</span>
              <span className="mono">{page.template_key}</span>
              <span>
                {new Intl.DateTimeFormat("ru", { dateStyle: "medium" }).format(
                  new Date(page.updated_at),
                )}
              </span>
              <ArrowUpRightIcon size={18} />
            </Link>
          ))}
        </section>
      ) : (
        <section className="page-list page-list--archive">
          <div className="page-list__head page-list__head--archive">
            <span>Название</span>
            <span>Ключ</span>
            <span>Шаблон</span>
            <span>Архивировано</span>
            <span>Действия</span>
          </div>
          {pages.map((page) => (
            <div
              className="page-list__row page-list__row--archive"
              key={page.id}
            >
              <span className="page-name">
                <i>
                  <ArchiveBoxIcon size={19} />
                </i>
                <strong>{page.internal_name}</strong>
              </span>
              <span className="mono">{page.page_key}</span>
              <span className="mono">{page.template_key}</span>
              <span>
                {new Intl.DateTimeFormat("ru", { dateStyle: "medium" }).format(
                  new Date(page.updated_at),
                )}
              </span>
              <span className="row-actions">
                <Button
                  disabled={Boolean(busyId)}
                  onClick={() => void restorePage(page)}
                  tone="quiet"
                >
                  <ArrowCounterClockwiseIcon size={16} /> Восстановить
                </Button>
                <Button
                  disabled={Boolean(busyId)}
                  onClick={() => void deletePermanently(page)}
                  tone="danger"
                >
                  <TrashIcon size={16} /> Удалить
                </Button>
              </span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function CreatePage({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [internalName, setInternalName] = useState("");
  const [pageKey, setPageKey] = useState("");
  const [templateKey, setTemplateKey] = useState("flexible");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const page = await apiRequest<Page>("/api/v1/admin/pages", {
        method: "POST",
        body: JSON.stringify({
          internal_name: internalName,
          page_key: pageKey,
          template_key: templateKey,
        }),
      });
      navigate(`/pages/${page.id}`);
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось создать страницу",
      );
      setSaving(false);
    }
  }

  return (
    <form className="inline-create" onSubmit={submit}>
      <div className="inline-create__intro">
        <FilePlusIcon size={22} />
        <div>
          <strong>Новая страница</strong>
          <span>Внутреннее имя не показывается посетителям.</span>
        </div>
      </div>
      <div className="inline-create__fields inline-create__fields--page">
        <TextField
          label="Название"
          onChange={(e) => setInternalName(e.target.value)}
          placeholder="О студии"
          required
          value={internalName}
        />
        <TextField
          helper="Стабильный системный идентификатор"
          label="Ключ"
          onChange={(e) => setPageKey(e.target.value)}
          placeholder="studio"
          required
          value={pageKey}
        />
        <SelectField
          label="Шаблон"
          onChange={(e) => setTemplateKey(e.target.value)}
          value={templateKey}
        >
          <option value="flexible">Свободная страница</option>
          <option value="home">Главная — locked</option>
          <option value="contact">Контакты</option>
          <option value="project">Кейс</option>
        </SelectField>
      </div>
      {error && <span className="field__error">{error}</span>}
      <div className="inline-create__actions">
        <Button onClick={onCancel} tone="quiet" type="button">
          Отмена
        </Button>
        <Button disabled={saving} type="submit">
          Создать
        </Button>
      </div>
    </form>
  );
}
